import { useCallback, useMemo } from 'react';
import { z } from 'zod';

import { useQueryParams } from './use-query-params';

const PaginationQuerySchema = z.object({
  page: z.number({ coerce: true }).default(0),
  page_size: z.number({ coerce: true }).default(10),
});

export type Pagination = {
  total: number;
  page: number;
  pageSize: number;
  goTo: (page: number) => void;
  prev?: () => void;
  next?: () => void;
};

export function usePagination<R>(results: R[]): [R[], Pagination] {
  const [{ page, page_size: pageSize }, queryParams] = useQueryParams(
    PaginationQuerySchema,
  );

  const total = results.length;
  const minPage = 0;
  const maxPage = Math.ceil(total / pageSize) - 1;

  const goTo = useCallback(
    (page: number) =>
      queryParams.push({
        page: Math.max(minPage, Math.min(page, maxPage)),
      }),
    [queryParams, minPage, maxPage],
  );

  const prev = useMemo(
    () => (page - 1 < minPage ? undefined : () => goTo(page - 1)),
    [goTo, minPage, page],
  );
  const next = useMemo(
    () => (page + 1 > maxPage ? undefined : () => goTo(page + 1)),
    [goTo, maxPage, page],
  );

  const paginatedResults = useMemo<R[]>(
    function () {
      const start = page * pageSize;
      const end = start + pageSize;

      return results.slice(start, end);
    },
    [results, page, pageSize],
  );

  return [
    paginatedResults,
    {
      total,
      page,
      pageSize,
      goTo,
      prev,
      next,
    },
  ];
}
